import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { X } from "@phosphor-icons/react"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { useAgentOptions } from "@/hooks/useAgentOptions"
import {
  SharedMedicalRecordEvent,
  useSharedMedicalRecordEventsStore,
} from "@/modules/patients/stores/useSharedMedicalRecordEventsStore"
import { useSharedMedicalRecordNoteStore } from "@/modules/patients/stores/useSharedMedicalRecordNoteStore"
import { useAppointmentContext } from "@/modules/waiting-room/contexts/appointment-context"

export const ShareMedicalRecordWarnings = () => {
  const sharedMedicalRecordEventsByPatientId = useSharedMedicalRecordEventsStore(
    (state) => state.sharedMedicalRecordEventsByPatientId,
  )

  return Object.values(sharedMedicalRecordEventsByPatientId).map((sharedMedicalRecordEvent) => (
    <ShareMedicalRecordWarning
      key={sharedMedicalRecordEvent.patient.id}
      patient={sharedMedicalRecordEvent.patient}
      requestorUserName={sharedMedicalRecordEvent.requestorUserName}
      waitingRoomAppointmentId={sharedMedicalRecordEvent.waitingRoomAppointmentId}
      temporaryNote={sharedMedicalRecordEvent.temporaryNote}
    />
  ))
}

const ShareMedicalRecordWarning = ({
  patient,
  requestorUserName,
  waitingRoomAppointmentId,
  temporaryNote,
}: {
  patient: SharedMedicalRecordEvent["patient"]
  requestorUserName: string
  waitingRoomAppointmentId: string | null
  temporaryNote: string | null
}) => {
  const { t } = useTranslation()
  const agentOptions = useAgentOptions()
  const setWaitingRoomPreviewDialog = useAppointmentContext((state) => state.setWaitingRoomPreviewDialog)
  const setSelectedWaitingRoomAppointmentId = useAppointmentContext(
    (state) => state.setSelectedWaitingRoomAppointmentId,
  )
  const setPreviewedSharedMedicalRecordPatientId = useSharedMedicalRecordNoteStore(
    (state) => state.setPreviewedSharedMedicalRecordPatientId,
  )
  const setSharedMedicalRecordNote = useSharedMedicalRecordNoteStore((state) => state.setSharedMedicalRecordNote)
  const removeSharedMedicalRecordNote = useSharedMedicalRecordNoteStore((state) => state.removeSharedMedicalRecordNote)
  const removeSharedMedicalRecordEvent = useSharedMedicalRecordEventsStore(
    (state) => state.removeSharedMedicalRecordEvent,
  )

  const { id: patientId, country, firstName, lastName } = patient
  const openMedicalRecord = useCallback(() => {
    if (!window) return
    window.open(`/patients/${country}/${patientId}`, patientId)
  }, [country, patientId])

  const openMedicalRecordPreview = useCallback(() => {
    setWaitingRoomPreviewDialog(true, true)
    setSelectedWaitingRoomAppointmentId(waitingRoomAppointmentId)
    setPreviewedSharedMedicalRecordPatientId(patientId)
  }, [
    waitingRoomAppointmentId,
    patientId,
    setWaitingRoomPreviewDialog,
    setSelectedWaitingRoomAppointmentId,
    setPreviewedSharedMedicalRecordPatientId,
  ])

  const requestorDisplayName =
    agentOptions.find((agentOption) => agentOption.value === requestorUserName)?.label ?? requestorUserName

  return (
    <div className="bg-background-subdued grid px-8">
      <Banner
        type="inline"
        variant="critical"
        className="py-1"
        iconClassName="self-auto"
        data-testid="share-medical-record-banner">
        <div className="flex grow items-center justify-between">
          <p className="whitespace-nowrap">
            {t("patient.shareMedicalRecord.meHasBeenShared", {
              agent: requestorDisplayName,
              patient: `${firstName} ${lastName}`,
            })}
          </p>
          <div className="flex">
            <Button
              variant="tertiary"
              className="text-on-critical-surface h-9 px-4.5 py-2"
              onClick={() => {
                if (temporaryNote) {
                  setSharedMedicalRecordNote(patientId, temporaryNote)
                }
                if (waitingRoomAppointmentId) {
                  openMedicalRecordPreview()
                } else {
                  openMedicalRecord()
                  removeSharedMedicalRecordNote(patientId)
                }

                removeSharedMedicalRecordEvent(patientId)
              }}
              data-testid="share-medical-record-banner-open-view-mode-button">
              {t("patient.shareMedicalRecord.openInViewMode")}
            </Button>
            <Button
              variant="circle"
              className="text-on-critical-surface h-9 w-9 cursor-pointer py-2"
              onClick={() => {
                removeSharedMedicalRecordNote(patientId)
                removeSharedMedicalRecordEvent(patientId)
              }}
              data-testid="share-medical-record-banner-close-button">
              <X />
            </Button>
          </div>
        </div>
      </Banner>
    </div>
  )
}
